import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="tentative-calculate"
import {
  fetchDiePriceTableFromId,
  fetchDiePriceTable,
  fetchForgingPriceTable,
  fetchMachinePartsPriceTable,
  fetchNimdenPriceTable
} from "./utils/fetch_price_table";

export default class extends Controller {
  static targets = [
    "showParamsOf",
    "diePriceTableId",
    "branchId",
    "weight",
    "area",
    "length",
    "width",
    "height",
    "maxPlatingThickness",
    "procedureType",
    "productType",
    "unitPrice",
    "dmUnitPrice"
  ];
  connect() {
    console.log("tentativeCalculateController connected");
  }

  async call(event) {
    // No jQuery and ajax use Stimulus, vanilla JS and fetch instead
    if (this.showParamsOfTarget.value === "金型") {
      const id = this.diePriceTableIdTarget.value;
      const t = this.weightTarget.value;
      const dm = this.areaTarget.value;
      const price = await fetchDiePriceTableFromId(t, dm, id);
      this.unitPriceTarget.value = price;
    } else {
      const procedure_type = this.procedureTypeTarget.value;
      const product_type = this.productTypeTarget.value;
      const branch_id = this.branchIdTarget.value;
      const length = this.lengthTarget.value;
      const width = this.widthTarget.value;
      const height = this.heightTarget.value;
      const max_plating_thickness = this.maxPlatingThicknessTarget.value;
      const t = this.hasWeightTarget ? this.weightTarget.value : 0;
      const dm = this.areaTarget.value;

      let price = 0;
      if (this.procedureTypeTarget.value === "無電解ニッケル") {
        price = await fetchNimdenPriceTable(dm, max_plating_thickness);
        this.unitPriceTarget.value = price;
      } else if (this.productTypeTarget.value === "プレス金型") {
        price = await fetchDiePriceTable(t, dm, branch_id);
        this.unitPriceTarget.value = price;
      } else if (this.productTypeTarget.value === "鍛造型") {
        price = await fetchForgingPriceTable(length, width, branch_id);
        this.unitPriceTarget.value = price;
      } else if (this.productTypeTarget.value === "機械部品") {
        price = await fetchMachinePartsPriceTable(
          dm,
          branch_id,
          max_plating_thickness
        );
        this.unitPriceTarget.value = price;
      } else if (this.productTypeTarget.value === "プレス（小物）") {
        if (!this.dmUnitPriceTarget.value) {
          const branch_id = this.branchIdTarget.value;
          const branch = await fetch(`/branches/${branch_id}`).then(
            (response) => response.json()
          );
          const client = branch.client;
          const smallUnitPrice = parseInt(client.small_apiece) || 0;

          this.dmUnitPriceTarget.value = smallUnitPrice;
        }
        const dmUnitPrice = this.dmUnitPriceTarget.value;
        const area = parseFloat(this.areaTarget.value) || 0;
        price = (dmUnitPrice * area).toFixed(0);
        this.unitPriceTarget.value = price;
      }
    }
  }

  /////////////////////////
  // Geometry Calculator //
  /////////////////////////

  ceiledValue(area, scale) {
    const scaledValue = area * scale * 0.0001 * 10;
    return Math.ceil(scaledValue) / 10;
  }

  cylinder(geometry_calculator_id) {
    const outerDiameter = parseInt(
      this.element.querySelector("[id$=_length]").value
    );
    const length = parseInt(this.element.querySelector("[id$=_width]").value);
    const pi = Math.PI;

    const radius = outerDiameter / 2;
    const radiusArea = radius ** 2 * pi;

    function calculateArea(geometryId) {
      const baseArea = outerDiameter * pi * length;

      switch (geometryId) {
        case 1:
          return baseArea;
        case 2:
          return baseArea + radiusArea;
        case 3:
          return baseArea + 2 * radiusArea;
        case 4:
          return 2 * baseArea + radiusArea;
        case 5:
          return 2 * baseArea + 2 * radiusArea;
        default:
          return 0;
      }
    }
    const scale =
      parseFloat(this.element.querySelector("[id$=_scale]").value) || 0;
    const area = calculateArea(geometry_calculator_id) || 0;

    this.element.querySelector("[id$=_area]").value = this.ceiledValue(
      area,
      scale
    );
  }

  block(geometry_calculator_id) {
    const middle = parseInt(this.element.querySelector("[id$=_length]").value);
    const large = parseInt(this.element.querySelector("[id$=_width]").value);
    const small = parseInt(this.element.querySelector("[id$=_height]").value);
    let area = 0;

    function calculateArea(geometryId) {
      switch (geometryId) {
        case 6:
          return (small + middle) * large;
        case 7:
          return (small * 2 + middle) * large;
        case 8:
          return (small + middle) * 2 * large;
        default:
          return 0;
      }
    }

    const scale = this.element.querySelector("[id$=_scale]").value;
    area = calculateArea(geometry_calculator_id);

    this.element.querySelector("[id$=_area]").value = this.ceiledValue(
      area,
      scale
    );
  }

  calculateArea() {
    const geometry_calculator_id = parseInt(
      this.element.querySelector("select[id$=_geometry_calculator_id]").value
    );
    if (geometry_calculator_id <= 5) {
      this.cylinder(geometry_calculator_id);
    } else {
      this.block(geometry_calculator_id);
    }
  }

  // 寸法Ａ	寸法Ｂ	寸法Ｃ	面積	計算式
  // 外径	長さ
  // 100	500		15.7ｄ㎡	外径×3.14×長さ＊（ｄ㎡換算÷10000）
  // 500	500		98.1ｄ㎡	（外径×3.14×長さ+外径の半径＊外径の半径＊3.14）＊（ｄ㎡換算÷10000）
  // 500	300		204.1ｄ㎡	（外径×3.14×長さ+外径の半径×外径の半径×3.14）×2×（ｄ㎡換算÷10000）
  // 500	500		176.6ｄ㎡	外径×3.14×長さ＊2+外径の半径＊外径の半径×3.14×（ｄ㎡換算÷10000）
  // 500	500		196.3ｄ㎡	（外径×3.14×長さ+外径の半径×外径の半径×3.14）×2×（ｄ㎡換算÷10000）
  // 寸法Ａ	寸法Ｂ	寸法Ｃ	面積	計算式
  // 中	大	小
  // 300	500	200	25.0ｄ㎡	（中×小）×大×（ｄ㎡換算÷10000）
  // 300	500	200	35.0ｄ㎡	（小×2+中）×大×（ｄ㎡換算÷10000）
  // 300	500	200	50.0ｄ㎡	（小+中）×2×大×（ｄ㎡換算÷10000）
}
